/**
 * Global toast hardening.
 *
 * FastAPI returns structured error bodies — a plain string, a 422 validation
 * ARRAY, or a dict like {error: "plan_expired", message: "..."}. Hundreds of
 * call sites do `toast.error(err?.response?.data?.detail || "fallback")`, and
 * handing sonner a non-string object crashes the render with
 * "Objects are not valid as a React child", white-screening the page behind an
 * uncaught-error overlay.
 *
 * Rather than patch every call site, we wrap sonner's toast methods once at
 * boot so any content that React cannot render is coerced to a readable
 * sentence first. React elements, strings, numbers and render functions pass
 * through untouched.
 */
import React from "react";
import { toast } from "sonner";

function isRenderable(v) {
  return (
    v == null ||
    typeof v === "string" ||
    typeof v === "number" ||
    typeof v === "boolean" ||
    typeof v === "function" ||
    React.isValidElement(v)
  );
}

export function toRenderableMessage(value) {
  if (isRenderable(value)) return value;

  // 422 validation payload: [{loc, msg, type}, ...]
  if (Array.isArray(value)) {
    const parts = value
      .map((v) => (isRenderable(v) ? v : toRenderableMessage(v)))
      .filter((v) => typeof v === "string" && v);
    return parts.join(" ") || "Something went wrong. Please try again.";
  }

  if (typeof value === "object") {
    // Preferred human-readable keys, in order, before falling back to JSON.
    for (const key of ["message", "msg", "detail", "error", "title"]) {
      const inner = value[key];
      if (typeof inner === "string" && inner) return inner;
    }
    try {
      const json = JSON.stringify(value);
      if (json && json !== "{}") return json;
    } catch {
      /* circular — fall through */
    }
  }

  return "Something went wrong. Please try again.";
}

const METHODS = ["error", "success", "info", "warning", "message", "loading"];
let installed = false;

export function installSafeToast() {
  if (installed) return;
  installed = true;
  METHODS.forEach((name) => {
    const original = toast[name];
    if (typeof original !== "function") return;
    toast[name] = (content, options) => original(toRenderableMessage(content), options);
  });
}

export default installSafeToast;
